// src/components/LeagueLeaderboardModal.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/Leaderboard.css"; // reuse your existing styles
import "../styles/LoginModal.css"; // reuse your existing modal styles
import "../styles/Leagues.css"; // reuse your existing league styles
// import "../styles/LeagueLeaderboardModal.css"; // optional modal-specific styling

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const LeagueLeaderboardModal = ({ show, onHide, leagueId, leagueName, currentUserId }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [userPicks, setUserPicks] = useState({});
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    if (!show || !leagueId) return;

    const fetchLeaderboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("authToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API_BASE_URL}/api/userStats/leaderboard`, {
          headers,
          params: { leagueId },
        });
        const data = res.data.map((u, idx) => ({ ...u, leagueRank: idx + 1 }));
        setLeaderboardData(data);
        setFilteredData(data);
      } catch (err) {
        console.error("Error fetching leaderboard:", err);
        setError("Failed to load leaderboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();

    const onResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [show, leagueId]);

  const computeColSpan = () => {
    let cols = 4; // arrow, rank, username, score
    if (!isMobile) cols += 3; // pointsWagered, conversionRate, pointsAvailable
    if (leagueId) cols += 1;
    cols += 1; // potential score
    return cols;
  };

  const handleRowClick = async (index, userId) => {
    const willCollapse = expandedRow === index;
    setExpandedRow(willCollapse ? null : index);
    if (willCollapse || userPicks[userId]) return;

    try {
      setLoadingUserId(userId);
      const res = await axios.get(`${API_BASE_URL}/api/picks/user/${userId}/picks-and-stats`);
      const picks = Array.isArray(res.data.picks) ? res.data.picks : [];
      const normalized = picks.map((p) => ({
        gameName: p.gameName,
        userPick: p.selectedWinner ?? p.userPick,
        pointsWagered: p.pointsWagered ?? p.confidence ?? 0,
        winner: p.winner,
        isCorrect: typeof p.isCorrect === "boolean" ? p.isCorrect : (p.selectedWinner ?? p.userPick) === p.winner,
      }));
      setUserPicks((prev) => ({ ...prev, [userId]: normalized }));
    } catch (err) {
      console.error(`Error loading picks for user ${userId}:`, err);
      setUserPicks((prev) => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingUserId(null);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-content-custom">
        <div className="modal-header-custom">
            <h4 className="modal-title">{leagueName} Leaderboard</h4>
            <button className="btn-close" onClick={onHide}></button>
        </div>
        {loading && <p>Loading...</p>}
        {error && <p className="error">{error}</p>}

        {!loading && !error && filteredData.length > 0 && (
          <AnimatePresence mode="wait">
            <motion.div
              key={leagueId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th />
                    <th>Conf Rank</th>
                    <th>Username</th>
                    <th>Score</th>
                    {!isMobile && <th>Points Wagered</th>}
                    {!isMobile && <th>Conversion Rate</th>}
                    {!isMobile && <th>Points Available</th>}
                    {!isMobile && <th>Potential Score</th>}
                    {/* <th>Potential Score</th> */}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((user, idx) => {
                    const isExpanded = expandedRow === idx;
                    const picks = userPicks[user.userId] || [];
                    const colSpan = computeColSpan();

                    return (
                      <React.Fragment key={user.userId}>
                        <tr
                          className={`leaderboard-row ${isExpanded ? "expanded" : ""} ${
                            user.userId === currentUserId ? "current-user" : ""
                          }`}
                          onClick={() => handleRowClick(idx, user.userId)}
                        >
                          <td className="expand-cell">
                            <span className={`expand-arrow ${isExpanded ? "open" : ""}`} aria-hidden>
                              ▶
                            </span>
                            <span className="sr-only">{isExpanded ? "Collapse" : "Expand"}</span>
                          </td>
                          <td>{user.leagueRank ?? "-"}</td>
                          <td>{user.username}</td>
                          <td>{user.score ?? 0}</td>
                          {!isMobile && <td>{user.pointsWagered ?? 0}</td>}
                          {!isMobile && <td>{user.conversionRate ?? "-"}</td>}
                          {!isMobile && <td>{user.pointsAvailable ?? 0}</td>}
                          {!isMobile && <td>{(user.score ?? 0) + (user.pointsAvailable ?? 0)}</td>}
                        </tr>

                       {isExpanded && (
                        <tr className="expanded-row">
                            <td colSpan={colSpan}>
                            <div className="expanded-content">

                                {/* Only show hidden columns if we're on mobile */}
                                {isMobile && (
                                <table className="expanded-stats-table">
                                    <tbody>
                                    <tr>
                                        <td>Points Wagered:</td>
                                        <td>{user.pointsWagered ?? 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Conversion Rate:</td>
                                        <td>{user.conversionRate ?? '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Points Available:</td>
                                        <td>{user.pointsAvailable ?? 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Potential Score:</td>
                                        <td>{(user.score ?? 0) + (user.pointsAvailable ?? 0)}</td>
                                    </tr>
                                    </tbody>
                                </table>
                                )}

                                {/* Picks table stays the same */}
                                {loadingUserId === user.userId ? (
                                <div>Loading picks...</div>
                                ) : picks.length === 0 ? (
                                <div>No picks available for this user.</div>
                                ) : (
                                <table className="picks-table">
                                    <thead>
                                    <tr className="expanded-tr">
                                        <th>Game</th>
                                        <th>Pick</th>
                                        <th>Wagered</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {picks.map((p, i) => {
                                        const isCorrect =
                                        typeof p.isCorrect === "boolean"
                                            ? p.isCorrect
                                            : (p.userPick ?? p.selectedWinner) === p.winner;

                                        let rowClass = "";
                                        if (p.winner === "TBD") rowClass = "gray-italic";
                                        else if (!isCorrect) rowClass = "incorrect";

                                        return (
                                        <tr key={i} className={rowClass}>
                                            <td>{p.gameName}</td>
                                            <td>{p.userPick}</td>
                                            <td className={`points-wagered ${isCorrect ? "correct" : ""}`}>
                                            {p.pointsWagered}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                                )}
                            </div>
                            </td>
                        </tr>
                        )}

                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default LeagueLeaderboardModal;
